/**
 * Wishlist Heart Toggle — Web Component
 * Saves/removes product variants from localStorage wishlist.
 * Dispatches wishlist:add and wishlist:remove events for other components.
 *
 * Data structure per item (sync-ready for future metafield integration):
 * { productId, variantId, handle, title, variantTitle, image, price, url, finishHandle, addedAt }
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABWishlistHeart extends ABBaseComponent {
  static STORAGE_KEY = 'ab-wishlist';
  static MAX_ITEMS = 200;

  connectedCallback() {
    this.btn = this.$('.ab-wishlist-heart__btn');
    if (!this.btn) return;

    this.variantId = this.dataset.variantId;
    if (!this.variantId) return;

    // Set initial state from localStorage
    if (this.isSaved()) {
      this.setActive(true);
    }

    this.btn.addEventListener('click', this.handleClick.bind(this));

    // Listen for cross-component updates (e.g., removed from wishlist tab)
    this._onRemove = (e) => {
      if (e.detail?.variantId === this.variantId) {
        this.setActive(false);
      }
    };
    document.addEventListener('wishlist:remove', this._onRemove);
  }

  disconnectedCallback() {
    if (this._onRemove) {
      document.removeEventListener('wishlist:remove', this._onRemove);
    }
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent card link navigation

    if (this.isSaved()) {
      this.removeItem();
      this.setActive(false);
      document.dispatchEvent(new CustomEvent('wishlist:remove', {
        bubbles: true,
        detail: { variantId: this.variantId }
      }));
    } else {
      this.addItem();
      this.setActive(true);
      document.dispatchEvent(new CustomEvent('wishlist:add', {
        bubbles: true,
        detail: { variantId: this.variantId }
      }));
    }
  }

  setActive(active) {
    this.classList.toggle('ab-wishlist-heart--active', active);
    if (this.btn) {
      this.btn.setAttribute('aria-pressed', String(active));
      // Update text for lg variant
      const textEl = this.btn.querySelector('.ab-wishlist-heart__text');
      if (textEl) {
        textEl.textContent = active ? 'Saved' : 'Save to Wishlist';
      }
    }
  }

  isSaved() {
    const items = this.getItems();
    return items.some(item => String(item.variantId) === String(this.variantId));
  }

  addItem() {
    try {
      const items = this.getItems();

      // Already saved — skip
      if (items.some(item => String(item.variantId) === String(this.variantId))) return;

      // Enforce max
      if (items.length >= ABWishlistHeart.MAX_ITEMS) {
        items.pop(); // Remove oldest
      }

      items.unshift({
        productId: this.dataset.productId,
        variantId: this.dataset.variantId,
        handle: this.dataset.productHandle,
        title: this.dataset.productTitle || '',
        variantTitle: this.dataset.variantTitle || '',
        image: this.dataset.productImage || '',
        price: this.dataset.productPrice || '',
        url: this.dataset.productUrl || '',
        finishHandle: this.dataset.finishHandle || '',
        addedAt: Date.now()
      });

      localStorage.setItem(ABWishlistHeart.STORAGE_KEY, JSON.stringify(items));
    } catch {
      // localStorage unavailable — silently fail
    }
  }

  removeItem() {
    try {
      const items = this.getItems();
      const filtered = items.filter(item => String(item.variantId) !== String(this.variantId));
      localStorage.setItem(ABWishlistHeart.STORAGE_KEY, JSON.stringify(filtered));
    } catch {
      // localStorage unavailable — silently fail
    }
  }

  getItems() {
    try {
      const raw = localStorage.getItem(ABWishlistHeart.STORAGE_KEY);
      if (!raw) return [];
      const items = JSON.parse(raw);
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }
}

defineComponent('ab-wishlist-heart', ABWishlistHeart);
